import SentryManager from '@widgets/sentry-manager';
import PostHogManager from '@widgets/posthog-manager';
import GTManager from '@widgets/gtm';
import SupportChat from '@widgets/support-chat';
import { store } from 'store';
import { authConstants } from '@constants/user';
import { accountConstants } from '@constants/account';
import { authService, hubService } from '@services';
import { clearPendingExports } from '@hooks/useExportNotifications';
import { getSessionIdentity, getStoredToken, getTokenClaims, setUserInLocalStorage } from '@helpers/auth';

export const authActions = {
    setUserFail,
    logout,
    getCurrentUser,
    endImpersonation,
    renewSession,
    resetStore,
    setUser,
    patchUser,
    setUserLoading,
};

const success = user => ({ type: authConstants.LOGIN_SUCCESS, user });

const failure = error => ({ type: authConstants.LOGIN_FAILURE, error });

function getCurrentUser() {
    return dispatch => {
        const requestToken = getStoredToken();
        const sessionRequest = authService
            .getCurrentSession()
            .catch(error => (getTokenClaims(requestToken)?.imp ? Promise.reject(error) : null));
        return Promise.all([authService.getCurrentUser(), sessionRequest]).then(
            ([user, session]) => {
                const impersonation = session?.impersonation
                    ? { ...session.impersonation, endsAt: Date.now() + session.impersonation.secondsLeft * 1000 }
                    : null;
                storeForSameSession(requestToken, session?.token ? { ...user, token: session.token } : user);
                PostHogManager.identify(user, impersonation);
                SentryManager.setUser(user);
                dispatch(setImpersonation(impersonation));
                dispatch(success(user));
            },
            () => {
                SupportChat.forget();
                dispatch(failure());
            },
        );
    };
}

function renewSession() {
    const requestToken = getStoredToken();
    return authService.getCurrentSession().then(
        session => session?.token && storeForSameSession(requestToken, { token: session.token }),
        () => {},
    );
}

function storeForSameSession(requestToken, user) {
    if (getSessionIdentity(getStoredToken()) === getSessionIdentity(requestToken)) {
        setUserInLocalStorage(user);
    }
}

let endingImpersonation = null;

function endImpersonation() {
    const token = getStoredToken();
    if (!getTokenClaims(token)?.imp) {
        return Promise.resolve();
    }
    endingImpersonation ??= authService
        .endCurrentSession()
        .catch(() => {})
        .finally(() => {
            endingImpersonation = null;
            if (getStoredToken() === token) {
                localStorage.removeItem('user');
            }
        });
    return endingImpersonation;
}

function logout() {
    endImpersonation();
    localStorage.removeItem('user');
    // Clear the active-account selection so the next login doesn't send a previous user's account id.
    localStorage.removeItem('activeAccountId');
    // INFO: stop hub connection
    hubService.stopHub();
    clearPendingExports();
    SentryManager.clearUser();
    // Reset PostHog session on logout
    PostHogManager.reset();
    SupportChat.forget();
    GTManager.clearRegistrationType();
    store.dispatch(resetStore());
    accountConstants.BILLING_EXPIRED = 0;
    return { type: authConstants.LOGOUT };
}

function resetStore() {
    return { type: authConstants.CLEAR_STORE };
}

function setUser(user) {
    return { type: authConstants.SET_USER, user };
}

function patchUser(payload) {
    return { type: authConstants.PATCH_USER, payload };
}

function setUserFail() {
    return { type: authConstants.LOGIN_FAILURE };
}

function setImpersonation(impersonation) {
    return { type: authConstants.SET_IMPERSONATION, payload: impersonation };
}

function setUserLoading(isLoading) {
    return { type: authConstants.SET_USER_LOADING, payload: isLoading };
}
